import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download, Filter, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 10;

const AdminRiskFlagsPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchedData, setFetchedData] = useState<any[]>([]);
  // (audit 2026-06-11) — FILTER, EXPORT CSV, pagination and the 3 Quick
  // Actions cards were inert. Filters drive in-memory filtering of the
  // ledger; Quick Actions navigate to the page that owns the workflow.
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiService.request('/admin/risk-flags', 'GET');
        if (response.success && response.data) {
          setFetchedData(response.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const riskFlags = fetchedData.map((d: any) => ({
    id: d.id,
    provider: d.provider_name || d.provider_id || d.entity_id || d.provider || '—',
    flagType: d.flag_type || d.reason || d.description || '—',
    severity: (['HIGH', 'MED', 'LOW'].includes((d.severity || '').toUpperCase())
      ? d.severity.toUpperCase()
      : 'LOW'),
    flaggedOn: d.flagged_at || d.created_at
      ? new Date(d.flagged_at || d.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
      : '—',
    status: (['OPEN', 'INVESTIGATING', 'RESOLVED'].includes((d.status || '').toUpperCase())
      ? d.status.toUpperCase()
      : 'OPEN'),
    assignedTo: d.assigned_to_name || d.assigned_to || 'Unassigned'
  }));

  const filteredFlags = riskFlags
    .filter((f) => severityFilter === 'all' || f.severity === severityFilter)
    .filter((f) => statusFilter === 'all' || f.status === statusFilter);

  const totalPages = Math.max(1, Math.ceil(filteredFlags.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, filteredFlags.length);
  const pagedFlags = filteredFlags.slice(pageStart, pageEnd);

  useEffect(() => { setCurrentPage(1); }, [severityFilter, statusFilter]);

  const handleExportCSV = () => {
    if (filteredFlags.length === 0) { alert('Nothing to export.'); return; }
    const headers = ['id','provider','flagType','severity','flaggedOn','status','assignedTo'];
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"£{s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(','),
      ...filteredFlags.map((f: any) => headers.map((h) => escape(f[h])).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `risk-flags-£{new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeHighRisk = riskFlags.filter(f => f.severity === 'HIGH' && f.status !== 'RESOLVED').length;
  const pendingInvestigation = riskFlags.filter(f => f.status === 'INVESTIGATING' || f.status === 'OPEN').length;
  const resolvedCount = riskFlags.filter(f => f.status === 'RESOLVED').length;
  const automatedCard = {
    color: 'bg-gradient-to-br from-blue-600 to-blue-700',
    label: 'AUTOMATED CLEARANCES',
    value: String(resolvedCount),
    subtitle: 'Auto-resolved flags this cycle'
  };

  const getSeverityColor = (severity: string) => {
    const config: Record<string, { bg: string; text: string; dot: string }> = {
      HIGH: { bg: 'bg-red-100', text: 'text-red-700', dot: '● ' },
      MED: { bg: 'bg-orange-100', text: 'text-orange-700', dot: '● ' },
      LOW: { bg: 'bg-gray-100', text: 'text-gray-700', dot: '● ' }
    };
    const c = config[severity] || { bg: 'bg-gray-100', text: 'text-gray-700', dot: '● ' };
    return <Badge className={`£{c.bg} £{c.text} border-0`}>{c.dot}{severity}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { bg: string; text: string }> = {
      OPEN: { bg: 'bg-gray-100', text: 'text-gray-700' },
      INVESTIGATING: { bg: 'bg-blue-100', text: 'text-blue-700' },
      RESOLVED: { bg: 'bg-green-100', text: 'text-green-700' }
    };
    const c = config[status] || { bg: 'bg-gray-100', text: 'text-gray-700' };
    return <Badge className={`£{c.bg} £{c.text} border-0`}>{status}</Badge>;
  };

  return (
    <>
      <Helmet>
        <title>Risk Flags - VireWire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-8 py-8">
              <div className="mb-2 text-sm text-gray-600">
                <span>COMPLIANCE COMMAND</span>
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Risk Flags</h1>
                <p className="text-gray-600 max-w-2xl">High-density marketplace intelligence. Monitor provider behavior, document expiration, and transaction anomalies in real-time.</p>
              </div>
            </div>
          </div>

          {/* Metrics & Content */}
          <div className="max-w-7xl mx-auto px-8 py-8">
            <div className="grid grid-cols-3 gap-6 mb-8">
              {/* High Risk Card */}
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">ACTIVE HIGH RISK</p>
                  <p className="text-4xl font-bold text-red-600 mb-1">{activeHighRisk}</p>
                  <p className="text-xs text-gray-600">● High severity, unresolved</p>
                </CardContent>
              </Card>

              {/* Pending Investigation Card */}
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">PENDING INVESTIGATION</p>
                  <p className="text-4xl font-bold text-teal-600 mb-1">{pendingInvestigation}</p>
                  <p className="text-xs text-gray-600">● Open or under investigation</p>
                </CardContent>
              </Card>

              {/* Automated Clearances Card */}
              <Card className={`£{automatedCard.color} text-white border-0`}>
                <CardContent className="pt-6">
                  <p className="text-xs font-bold text-blue-100 uppercase tracking-widest mb-2">{automatedCard.label}</p>
                  <p className="text-4xl font-bold mb-2">{automatedCard.value}</p>
                  <p className="text-sm text-blue-100">{automatedCard.subtitle}</p>
                </CardContent>
              </Card>
            </div>

            {/* Live Risk Ledger */}
            <Card>
              <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50">
                <h2 className="text-lg font-bold text-gray-900">Live Risk Ledger</h2>
                <div className="flex items-center gap-3">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <select
                    value={severityFilter}
                    onChange={(e) => setSeverityFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-white text-sm bg-white"
                  >
                    <option value="all">ALL SEVERITY</option>
                    <option value="HIGH">HIGH</option>
                    <option value="MED">MED</option>
                    <option value="LOW">LOW</option>
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-white text-sm bg-white"
                  >
                    <option value="all">ALL STATUS</option>
                    <option value="OPEN">OPEN</option>
                    <option value="INVESTIGATING">INVESTIGATING</option>
                    <option value="RESOLVED">RESOLVED</option>
                  </select>
                  <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-white text-sm"
                  >
                    <Download className="w-4 h-4" />
                    EXPORT CSV
                  </button>
                </div>
              </div>

              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Provider / Job Id</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Flag Type</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Severity</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Flagged On</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Assigned To</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedFlags.length === 0 && (
                        <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">No risk flags found.</td></tr>
                      )}
                      {pagedFlags.map((flag) => (
                        <tr key={flag.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <p className="font-bold text-gray-900">{flag.provider}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-700">{flag.flagType}</p>
                          </td>
                          <td className="px-6 py-4">{getSeverityColor(flag.severity)}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{flag.flaggedOn}</td>
                          <td className="px-6 py-4">{getStatusBadge(flag.status)}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-gray-300 rounded-full flex-shrink-0"></div>
                              <p className="text-sm text-gray-900">{flag.assignedTo}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => navigate(`/admin/providers/£{flag.id}`)}
                              title="View provider"
                              className="text-gray-400 hover:text-gray-600"
                            >
                              •••
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-sm text-gray-600">
                    {filteredFlags.length === 0
                      ? 'No matching risk flags'
                      : `Showing £{pageStart + 1} to £{pageEnd} of £{filteredFlags.length} risk flags`}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      className="px-3 py-1 text-sm font-bold text-gray-600 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                    >
                      &lt;
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 5).map((n) => (
                      <button
                        key={n}
                        onClick={() => setCurrentPage(n)}
                        className={`w-8 h-8 text-sm font-bold rounded £{
                          n === currentPage
                            ? 'bg-teal-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="px-3 py-1 text-sm font-bold text-gray-600 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Severity Trend Analysis */}
            <div className="grid grid-cols-3 gap-8 mt-8">
              <div className="col-span-2">
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">Severity Trend Analysis</h3>
                    <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <div className="flex items-end gap-4 h-48">
                        {[
                          { day: 'MON', height: '40%', color: 'bg-teal-200' },
                          { day: 'TUE', height: '35%', color: 'bg-teal-200' },
                          { day: 'WED', height: '50%', color: 'bg-teal-200' },
                          { day: 'THU', height: '70%', color: 'bg-red-300' },
                          { day: 'FRI', height: '45%', color: 'bg-teal-200' },
                          { day: 'SAT', height: '38%', color: 'bg-teal-200' },
                          { day: 'SUN', height: '42%', color: 'bg-teal-200' }
                        ].map((bar) => (
                          <div key={bar.day} className="flex flex-col items-center">
                            <div className={`£{bar.color} w-12 rounded-t`} style={{ height: bar.height }}></div>
                            <p className="text-xs font-bold text-gray-600 mt-2">{bar.day}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="text-center text-xs text-gray-600 mt-4">THU (PEAK) - 45% incident increase during weekly billing cycle</p>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <div>
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">QUICK ACTIONS</h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => navigate('/admin/platform-rules?focus=auto-restriction')}
                        className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span>🛡️</span>
                          <span className="font-bold text-gray-900 text-sm">Auto-Restrict Filter</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </button>
                      <button
                        onClick={() => navigate('/admin/platform-rules?focus=flag-policies')}
                        className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span>📋</span>
                          <span className="font-bold text-gray-900 text-sm">Update Flag Policies</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </button>
                      <button
                        onClick={() => navigate('/admin/configuration-status?view=audit-log&entity=risk-flag')}
                        className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span>📝</span>
                          <span className="font-bold text-gray-900 text-sm">Compliance Log Review</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminRiskFlagsPage;
